import { useLiveQuery } from 'dexie-react-hooks';

import { aircraft, flights, logbookEntries, tracks } from '@/lib/db';

import { buildFlightSummaries, type FlightSummary } from './summary';

/**
 * Every flight, joined to its track, aircraft and logbook entry, live.
 *
 * One live query over four tables rather than a query per row: Dexie re-runs
 * the whole callback when any table it touched changes, so per-row lookups
 * would be N subscriptions that all re-fire together anyway. The caps match
 * `TrackList`'s existing trade-off — a fixed window into IndexedDB, not paging.
 *
 * @returns `null` while the database is opening, which is not the same thing as
 *   an empty list and must not render as "no flights yet" on the way past.
 */
export function useFlightSummaries(limit = 500): FlightSummary[] | null {
  return useLiveQuery(
    async () => {
      const [flightRows, trackRows, aircraftRows, entryRows] = await Promise.all([
        flights.listRecent(limit),
        tracks.listRecent(limit),
        aircraft.list(),
        logbookEntries.listOrdered(),
      ]);
      return buildFlightSummaries({
        flights: flightRows,
        tracks: trackRows,
        aircraft: aircraftRows,
        entries: entryRows,
      });
    },
    [limit],
    null,
  );
}

/**
 * The same join, narrowed to one aircraft — the aircraft page's history list.
 *
 * Narrowed *after* the join rather than before it. Fetching only the flights
 * and entries whose `aircraftId` column matches looked like the cheaper query
 * and was the wrong set: `aircraftId` is filled in at import time only when
 * the aeroplane is already in the register, and never backfilled onto a
 * confirmed entry (`backfillDraftsForAircraft` deliberately leaves a pilot's
 * approved record alone). A pilot who imported tracks first and added the
 * aircraft afterwards had flights and logbook entries whose only link to the
 * aeroplane was the registration on them — so the narrow queries came back
 * empty, the page fell through to the bare tracks, and every row read "No
 * entry" with no hours and no distance beside four flights that had all
 * three.
 *
 * `buildFlightSummaries` already resolves an aircraft by registration when
 * the id is missing, so joining everything and then keeping what belongs to
 * this aeroplane — by id on the flight, on the entry, or by the registration
 * the summary ended up with — is the set the page is asking for. The caps are
 * the same fixed window `useFlightSummaries` reads through.
 */
export function useAircraftFlightSummaries(
  aircraftId: string,
  limit = 500,
): FlightSummary[] | null {
  return useLiveQuery(
    async () => {
      const record = await aircraft.get(aircraftId);
      if (record === undefined) {
        return [];
      }
      const [flightRows, trackRows, aircraftRows, entryRows] = await Promise.all([
        flights.listRecent(limit),
        tracks.listRecent(limit),
        aircraft.list(),
        logbookEntries.listOrdered(),
      ]);
      /*
       * Tracks carrying this registration but no flight record belong here
       * too — they are flights in this aeroplane, imported before the aircraft
       * was on file or before flights were written at all, and an aircraft
       * page that hid them would be telling the pilot they never happened.
       */
      const summaries = buildFlightSummaries({
        flights: flightRows,
        tracks: trackRows,
        aircraft: aircraftRows,
        entries: entryRows,
      });
      return summaries.filter(
        (summary) =>
          summary.flight?.aircraftId === aircraftId ||
          summary.entry?.aircraftId === aircraftId ||
          summary.aircraft?.id === aircraftId ||
          summary.registration === record.registration,
      );
    },
    [aircraftId, limit],
    null,
  );
}

/**
 * One flight, joined, found by the track it was imported from.
 *
 * `null` while the database is opening; `undefined` when there is no such
 * track, which the detail page renders as "not found" rather than as a blank.
 */
export function useSummaryForTrack(trackId: string): FlightSummary | null | undefined {
  return useLiveQuery(
    async () => {
      const track = await tracks.get(trackId);
      if (track === undefined) {
        return undefined;
      }
      const flight = await flights.findByTrack(trackId);
      const [aircraftRows, entry] = await Promise.all([
        aircraft.list(),
        flight === undefined ? undefined : logbookEntries.findByFlight(flight.id),
      ]);
      const [summary] = buildFlightSummaries({
        flights: flight === undefined ? [] : [flight],
        tracks: [track],
        aircraft: aircraftRows,
        entries: entry === undefined ? [] : [entry],
      });
      return summary;
    },
    [trackId],
    null,
  );
}
